import type { Ref } from "vue";

import { useQuery } from "@pinia/colada";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { useApiGetWidgetData } from "@/shared/model";

import type { DeviceBrand, DeviceCategory, DeviceModel, DeviceProduct } from "../api";

import { getDeviceDeps, getDeviceModels, getDeviceProducts } from "../api";

/** Категория "PC Notebook Commercial": вместо памяти и цвета выбираются характеристики */
const COMMERCIAL_NOTEBOOK_CATEGORY = { id: "367", name: "PC Notebook Commercial" };

const APPLE_BRAND_ID = "170161";

/** Бренды, которые показываются первыми и именно в этом порядке */
const PRIORITY_BRANDS = ["Apple", "Samsung"];

// The catalog doesn't change while the widget is open, so every response is cached for good
const CATALOG_CACHE = {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
} as const;

function normalizeBrands(brands: DeviceBrand[]): DeviceBrand[] {
    return (
        brands
            .filter((brand) => brand.name.toLowerCase() !== "other brands")
            .map(({ brand_id, name }) => ({
                brand_id,
                name: name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(),
            }))
            // map() already made a copy, and toSorted() is missing in browsers Vite still targets (Firefox < 115)
            // oxlint-disable-next-line unicorn/no-array-sort
            .sort((a, b) => {
                const aPriority = PRIORITY_BRANDS.indexOf(a.name);
                const bPriority = PRIORITY_BRANDS.indexOf(b.name);

                if (aPriority !== -1 || bPriority !== -1) {
                    if (aPriority === -1) return 1;
                    if (bPriority === -1) return -1;
                    return aPriority - bPriority;
                }

                return a.name.localeCompare(b.name);
            })
    );
}

function uniqueBy<T>(items: T[], key: keyof T): T[] {
    return items.filter(
        (item, index) => index === items.findIndex((other) => other[key] === item[key]),
    );
}

/** Сдаваемое устройство: выбор в каскаде "бренд → категория → модель → модификация" */
export const useDeviceStore = defineStore("device", () => {
    const { data: widgetData } = useApiGetWidgetData();
    const token = computed(() => widgetData.value?.token ?? "");

    // |-----------|
    // | SELECTION |
    // |-----------|

    const brand = ref<DeviceBrand | null>(null);
    const category = ref<DeviceCategory | null>(null);
    const model = ref<DeviceModel | null>(null);
    const storage = ref<DeviceProduct | null>(null);
    const color = ref<DeviceProduct | null>(null);
    // Commercial notebooks only
    const cpu = ref<DeviceProduct | null>(null);
    const gpu = ref<DeviceProduct | null>(null);
    const ram = ref<DeviceProduct | null>(null);
    const memory = ref<DeviceProduct | null>(null);

    /** Порядок каскада: изменение поля сбрасывает все поля после него */
    const cascade: Ref<unknown>[] = [brand, category, model, storage, color, cpu, gpu, ram, memory];

    // |---------|
    // | QUERIES |
    // |---------|

    const depsQuery = useQuery(() => ({
        key: ["device-deps", token.value],
        query: async () => {
            const deps = await getDeviceDeps({ token: token.value });
            return { ...deps, brands: normalizeBrands(deps.brands) };
        },
        enabled: Boolean(token.value),
        ...CATALOG_CACHE,
    }));

    const modelsQuery = useQuery(() => {
        const brandId = brand.value?.brand_id ?? "";
        const categoryId = category.value?.category_id ?? "";

        return {
            key: ["device-models", token.value, brandId, categoryId],
            query: () =>
                getDeviceModels({
                    token: token.value,
                    brand_id: brandId,
                    category_id: categoryId,
                }),
            enabled: Boolean(token.value && brandId && categoryId),
            ...CATALOG_CACHE,
        };
    });

    const productsQuery = useQuery(() => {
        const modelId = model.value?.model_id ?? "";

        return {
            key: ["device-products", token.value, modelId],
            query: () => getDeviceProducts({ token: token.value, model_id: modelId }),
            enabled: Boolean(token.value && modelId),
            ...CATALOG_CACHE,
        };
    });

    // |---------|
    // | OPTIONS |
    // |---------|

    const products = computed(() => (model.value && productsQuery.data.value) || []);

    const brandOptions = computed(() => depsQuery.data.value?.brands ?? []);

    const categoryOptions = computed<DeviceCategory[]>(() => {
        const deps = depsQuery.data.value;
        const selectedBrand = brand.value;

        if (!deps || !selectedBrand) return [];

        const categoryIds = new Set(
            deps.brands_categories
                .filter((item) => item.brand_id === selectedBrand.brand_id)
                .map((item) => item.category_id),
        );
        const categories = deps.categories.filter((item) => categoryIds.has(item.category_id));

        // iPhone goes first for Apple
        if (selectedBrand.brand_id === APPLE_BRAND_ID || selectedBrand.name === "Apple") {
            categories.sort((a, b) => {
                if (a.name.toLowerCase() === "iphone") return -1;
                if (b.name.toLowerCase() === "iphone") return 1;
                return 0;
            });
        }

        return categories;
    });

    const modelOptions = computed(
        () => (brand.value && category.value && modelsQuery.data.value) || [],
    );

    const storageOptions = computed(() => uniqueBy(products.value, "storage"));

    const colorOptions = computed(() =>
        storage.value
            ? products.value.filter((item) => item.storage === storage.value?.storage)
            : [],
    );

    const cpuOptions = computed(() => uniqueBy(products.value, "keyShow2"));

    const gpuOptions = computed(() =>
        cpu.value
            ? uniqueBy(
                  products.value.filter((item) => item.keyShow2 === cpu.value?.keyShow2),
                  "keyShow3",
              )
            : [],
    );

    const ramOptions = computed(() =>
        gpu.value
            ? uniqueBy(
                  products.value.filter(
                      (item) =>
                          item.keyShow2 === gpu.value?.keyShow2 &&
                          item.keyShow3 === gpu.value?.keyShow3,
                  ),
                  "keyShow4",
              )
            : [],
    );

    const memoryOptions = computed(() =>
        ram.value
            ? uniqueBy(
                  products.value.filter(
                      (item) =>
                          item.keyShow2 === ram.value?.keyShow2 &&
                          item.keyShow3 === ram.value?.keyShow3 &&
                          item.keyShow4 === ram.value?.keyShow4,
                  ),
                  "keyShow5",
              )
            : [],
    );

    // |----------|
    // | COMPUTED |
    // |----------|

    const isModelsLoading = computed(() => modelsQuery.asyncStatus.value === "loading");
    const isProductsLoading = computed(() => productsQuery.asyncStatus.value === "loading");

    const isCommercialNotebook = computed(
        () =>
            category.value?.category_id === COMMERCIAL_NOTEBOOK_CATEGORY.id ||
            category.value?.name === COMMERCIAL_NOTEBOOK_CATEGORY.name,
    );

    /** Выбранная модификация, по ней устройство регистрируется для оценки */
    const productId = computed(
        () => (isCommercialNotebook.value ? memory.value : color.value)?.productId ?? "",
    );

    /**
     * Цепочка характеристик устройства
     * @example ["Apple", "iPhone", "12 Pro Max", "256GB", "Graphite"]
     */
    const chain = computed(() =>
        [
            brand.value?.name,
            category.value?.name,
            model.value?.name,
            storage.value?.storage,
            color.value?.color,
            cpu.value?.keyShow2,
            gpu.value?.keyShow3,
            ram.value?.keyShow4,
            memory.value?.keyShow5,
        ].filter((item): item is string => Boolean(item)),
    );

    /** Выбраны все поля, нужные для текущей категории */
    const isComplete = computed(() => {
        const required = isCommercialNotebook.value
            ? [brand, category, model, cpu, gpu, ram, memory]
            : [brand, category, model, storage, color];

        return required.every((field) => Boolean(field.value));
    });

    // |----------|
    // | WATCHERS |
    // |----------|

    cascade.forEach((field, index) => {
        watch(field, () => {
            cascade.slice(index + 1).forEach((below) => (below.value = null));
        });
    });

    // Registered after the cascade reset, so it runs once the category is already cleared
    watch(brand, () => {
        const [onlyCategory, ...rest] = categoryOptions.value;
        if (onlyCategory && rest.length === 0) category.value = onlyCategory;
    });

    // |---------|
    // | METHODS |
    // |---------|

    function reset(): void {
        cascade.forEach((field) => (field.value = null));
    }

    return {
        brand,
        category,
        model,
        storage,
        color,
        cpu,
        gpu,
        ram,
        memory,

        brandOptions,
        categoryOptions,
        modelOptions,
        storageOptions,
        colorOptions,
        cpuOptions,
        gpuOptions,
        ramOptions,
        memoryOptions,

        isModelsLoading,
        isProductsLoading,
        isCommercialNotebook,
        isComplete,
        productId,
        chain,

        reset,
    };
});
